import React, { useMemo, useState, useEffect } from "react";
import Navbar from "../Components/Navbar";
import Sidebar from "../Components/Sidebar";
import { useThemeContext } from "../context/ThemeContext";
import {
  Box,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Paper,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ClockIcon from "@mui/icons-material/Schedule";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import EventIcon from "@mui/icons-material/Event";
import { useNavigate, useLocation } from "react-router-dom";

import { getJobStats, getTechnicians } from "../data/dummyJobs";
import axios from "../axios";
import CreateJobModal from "../Components/CreateJobModal";

const Jobs = () => {
  const { isDarkMode } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [searchTerm, setSearchTerm] = useState(params.get('q') || params.get('ref') || "");
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(params.get('status') || "all");
  const [techFilter, setTechFilter] = useState(params.get('technician') || "all");

  const [jobsList, setJobsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [techUsers, setTechUsers] = useState([]);

  const normalizeJob = (j) => ({
    ...j,
    id: j._id || j.id,
    ref: j.jobNumber || j.ref || (j._id ? String(j._id) : ''),
    displayRef: j.jobNumber || j.ref || (j._id ? String(j._id).slice(-6) : ''),
    customerName: j.customerName || j.customer || '',
    customer: j.customerName || j.customer || '',
    createdByName: j.createdBy && (j.createdBy.firstName || j.createdBy.lastName) ? `${j.createdBy.firstName || ''} ${j.createdBy.lastName || ''}`.trim() : (j.createdByName || j.createdBy || ''),
    deviceName: j.deviceName || '',
    deviceModel: j.deviceModel || '',
    serialNumber: j.serialNumber || '',
    faultDescription: j.faultDescription || j.description || j.desc || '',
    amount: j.roughCost || j.amount || j.totalAmount || 0,
    usedParts: j.jobParts || j.usedParts || [],
    technician: (() => {
      if (!j.technicianId) return j.technician || j.assignedToName || '';
      if (typeof j.technicianId === 'object') {
        const fn = j.technicianId.firstName || '';
        const ln = j.technicianId.lastName || '';
        const name = (fn + ' ' + ln).trim();
        if (name) return name;
        return j.technicianId.email || j.technicianId._id || '';
      }
      return j.technicianId || j.technician || j.assignedToName || '';
    })(),
    technicianIdRaw: j.technicianId,
    createdAt: j.createdAt || j.date || ''
  });

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatPhone = (p) => {
    if (!p) return '';
    const s = String(p).trim();
    if (s.startsWith('+')) return s;
    if (s.startsWith('0')) return `+94${s.slice(1)}`;
    return s;
  };

  const formatAmount = (n) => {
    const v = Number(n) || 0;
    return v.toLocaleString('en-IN');
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const token = JSON.parse(localStorage.getItem('userInfo'))?.token;
    axios.get('/paneljob', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const payload = res.data;
        let docs = [];
        if (Array.isArray(payload)) docs = payload;
        else if (Array.isArray(payload?.data)) docs = payload.data;
        else if (Array.isArray(payload?.docs)) docs = payload.docs;
        else if (Array.isArray(payload?.result)) docs = payload.result;
        else docs = [];
        if (!mounted) return;
        setJobsList((docs || []).map(normalizeJob));
        // fetch users (to resolve technician ids to names)
        axios.get('/user/list').then((ures) => {
          if (!mounted) return;
          const users = Array.isArray(ures.data) ? ures.data : (Array.isArray(ures.data?.data) ? ures.data.data : []);
          setTechUsers(users.filter(x => x.userType === 'technician'));
        }).catch(() => {});
      })
      .catch((err) => {
        console.error('Failed to load jobs', err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  // listen for updates from job details (re-fetch jobs when a job is updated)
  useEffect(() => {
    let mounted = true;
    const handler = () => {
      if (!mounted) return;
      setLoading(true);
      const token = JSON.parse(localStorage.getItem('userInfo'))?.token;
      axios.get('/paneljob', { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          const payload = res.data;
          let docs = [];
          if (Array.isArray(payload)) docs = payload;
          else if (Array.isArray(payload?.data)) docs = payload.data;
          else if (Array.isArray(payload?.docs)) docs = payload.docs;
          else if (Array.isArray(payload?.result)) docs = payload.result;
          else docs = [];
          setJobsList((docs || []).map(normalizeJob));
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    };
    window.addEventListener('paneljob:updated', handler);
    return () => { mounted = false; window.removeEventListener('paneljob:updated', handler); };
  }, []);

  // base job number and helper to compute next increment
  const BASE_JOB_NUMBER = 30000;
  const getNextJobNumber = (jobs) => {
    let max = BASE_JOB_NUMBER - 1;
    (jobs || []).forEach((j) => {
      const s = (j.ref || j.id || "").toString();
      const digits = s.match(/\d+/g);
      if (digits) {
        const num = parseInt(digits.join(""), 10);
        if (!Number.isNaN(num) && num > max) max = num;
      }
    });
    return String(max + 1);
  };

  const [nextJobRef, setNextJobRef] = useState(getNextJobNumber([]));
  useEffect(() => setNextJobRef(getNextJobNumber(jobsList)), [jobsList]);

  const stats = useMemo(() => getJobStats(jobsList), [jobsList]);
  const technicians = useMemo(() => getTechnicians(jobsList), [jobsList]);
  const [createOpen, setCreateOpen] = useState(false);
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  const userTypeStr = (userInfo.userType || userInfo.role || '').toString().toLowerCase();
  const isTechnician = userTypeStr === 'technician';
  const [pendingOnly, setPendingOnly] = useState(false);
  const hideAssignedReady = useMemo(() => params.get('hideAssignedReady') === '1', [params]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setCategoryFilter(p.get('status') || 'all');
    setTechFilter(p.get('technician') || 'all');
    setSearchTerm(p.get('q') || p.get('ref') || '');
  }, [location.search]);

  const categories = ["all", "Pending", "In Progress", "Completed", "Assigned", "Ready"];

  const handleCreateJob = (jobData) => {
    const jobToAdd = { ...jobData };
    if (!jobToAdd.ref) jobToAdd.ref = getNextJobNumber(jobsList);
    const now = new Date().toISOString();
    jobToAdd.updatedAt = now;
    if (!jobToAdd.createdAt) jobToAdd.createdAt = now;
    setJobsList((prev) => [jobToAdd, ...(prev || [])]);
    setCreateOpen(false);
  };

  const handleReferNavigate = async (referNum) => {
    if (!referNum) return;
    // first try to find in already loaded jobs
    const foundLocal = (jobsList || []).find(d => String((d.jobNumber || d.ref || d.id || '')).toLowerCase() === String(referNum).toLowerCase());
    if (foundLocal) {
      navigate(`/jobDetails/${encodeURIComponent(foundLocal.id)}`);
      return;
    }
    // fallback: fetch list from server and try to find
    try {
      const token = JSON.parse(localStorage.getItem('userInfo'))?.token;
      const res = await axios.get('/paneljob', { headers: { Authorization: `Bearer ${token}` } });
      const payload = res.data;
      let docs = [];
      if (Array.isArray(payload)) docs = payload;
      else if (Array.isArray(payload?.data)) docs = payload.data;
      else if (Array.isArray(payload?.docs)) docs = payload.docs;
      else if (Array.isArray(payload?.result)) docs = payload.result;
      const found = (docs || []).find(d => String((d.jobNumber || d.ref || d._id || '')).toLowerCase() === String(referNum).toLowerCase());
      if (found) navigate(`/jobDetails/${encodeURIComponent(found._id)}`);
    } catch (e) {
      console.warn('Failed to navigate to refer job', e);
    }
  };

  const filteredJobs = useMemo(() => {
    return (jobsList || []).filter((j) => {
      if (categoryFilter !== "all" && j.status !== categoryFilter) return false;
      // For technicians, if they have not toggled the pending-only view, hide pending jobs from Recent Jobs
      if (isTechnician && !pendingOnly) {
        const s = (j.status || '').toString().toLowerCase();
        if (s.includes('pending')) return false;
      }
      if (techFilter !== "all" && j.technician !== techFilter) return false;
      const term = searchTerm.trim().toLowerCase();
      if (!term) return true;
      return (
        (j.ref || "").toLowerCase().includes(term) ||
        (j.customer || "").toLowerCase().includes(term) ||
        (j.desc || "").toLowerCase().includes(term) ||
        (j.status || "").toLowerCase().includes(term)
      );
    });
  }, [searchTerm, categoryFilter, techFilter, jobsList, isTechnician, pendingOnly]);

  const pendingJobs = useMemo(() => {
    return (jobsList || []).filter(j => {
      const s = (j.status || '').toString().toLowerCase();
      const isPending = s.includes('pending');
      const unassigned = !j.technicianIdRaw || (typeof j.technicianIdRaw === 'string' ? j.technicianIdRaw === '' : false);
      return isPending && unassigned;
    });
  }, [jobsList]);

  // consume newly created job passed via navigation state
  useEffect(() => {
    if (location && location.state && location.state.newJob) {
      const newJob = location.state.newJob;
      setJobsList((prev) => [newJob, ...(prev || [])]);
      // clear navigation state so it doesn't re-apply on back/refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleViewAll = () => {
    // match user request: do not navigate, just reset filters to show all dummy data
    setSearchTerm("");
    setCategoryFilter("all");
    setTechFilter("all");
    setShowFilters(false);
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc",
          minHeight: "100vh",
          pt: "80px",
          display: "flex",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        <Sidebar />

        <Box sx={{ flexGrow: 1, px: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 600, color: "#0f172a", mb: 1 }}>All Jobs</Typography>
                <Typography sx={{ fontSize: 14, color: "#64748b" }}>{stats.total} job{stats.total !== 1 ? 's' : ''}</Typography>
              </Box>

              {!isTechnician && (
                <Button
                  onClick={() => { setNextJobRef(getNextJobNumber(jobsList)); setCreateOpen(true); }}
                  sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#2563eb", color: "#fff", textTransform: "none", fontWeight: 500, borderRadius: "8px", px: 2, "&:hover": { backgroundColor: "#1d4ed8" } }}
                >
                  + Create Job
                </Button>
              )}
              
            </Box>

            

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2, mt: 2 }}>
              {(() => {
                const items = [
                  { title: "Total Jobs", value: stats.total, icon: <ViewInArIcon sx={{ color: "#0369a1" }} />, bg: "#e0f2fe" },
                  { title: "Pending", value: stats.pending, icon: <WarningAmberIcon sx={{ color: "#f59e0b" }} />, bg: "#fff7ed" },
                  { title: "In Progress", value: stats.inProgress, icon: <TrendingDownIcon sx={{ color: "#f97316" }} />, bg: "#ffedd5" },
                  { title: "Assigned", value: stats.assigned, icon: <ViewInArIcon sx={{ color: "#7c3aed" }} />, bg: "#eef2ff" },
                  { title: "Ready", value: stats.ready, icon: <ClockIcon sx={{ color: "#059669" }} />, bg: "#ecfdf5" }
                ];

                const filtered = items.filter(i => !((i.title === 'Assigned' || i.title === 'Ready') && hideAssignedReady));

                return filtered.map((item, i) => (
                  <Box key={i}>
                    <Card sx={{ borderRadius: "12px", border: "1px solid #e5e7eb" }}>
                      <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>{item.title}</Typography>
                          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{item.value}</Typography>
                        </Box>
                        <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: item.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{item.icon}</Box>
                      </CardContent>
                    </Card>
                  </Box>
                ));
              })()}
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 2, border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb", p: 3, mt: 3, backgroundColor: isDarkMode ? '#1e293b' : '#fff' }}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                <Box sx={{ position: "relative", flex: 1 }}>
                  <SearchIcon sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 16 }} />
                  <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by ref, customer, description, status..." style={{ width: "100%", padding: "10px 12px 10px 36px", borderRadius: 8, border: isDarkMode ? "1px solid #334155" : "1px solid #d1d5db", backgroundColor: isDarkMode ? '#0f172a' : '#fff', color: isDarkMode ? '#f1f5f9' : '#000' }} />
                  {searchTerm && (
                    <button onClick={() => setSearchTerm("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", color: "#64748b" }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </button>
                  )}
                </Box>

                <Button onClick={() => setShowFilters(!showFilters)} sx={{ borderRadius: 1, border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb", color: isDarkMode ? "#f1f5f9" : "#374151", textTransform: "none" }}>
                  <FilterListIcon sx={{ fontSize: 14, mr: 1 }} />
                  Filters
                </Button>
              </Box>

              {showFilters && (
                <Box sx={{ mt: 3, borderTop: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb", pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography sx={{ mb: 1, color: isDarkMode ? "#cbd5e1" : "#374151" }}>Status</Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {categories.map((cat) => (
                        <Button key={cat} onClick={() => setCategoryFilter(cat)} sx={{ textTransform: "none", borderRadius: 1, border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb", backgroundColor: categoryFilter === cat ? (isDarkMode ? "#0f4a8c" : "#eff6ff") : (isDarkMode ? "#1e293b" : "#fff"), color: isDarkMode ? "#f1f5f9" : "#000" }}>
                          {cat === "all" ? "All Statuses" : cat}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ mb: 1, color: isDarkMode ? "#cbd5e1" : "#374151" }}>Technician</Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button onClick={() => setTechFilter("all")} sx={{ textTransform: "none", borderRadius: 1, border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb", backgroundColor: techFilter === "all" ? (isDarkMode ? "#0f4a8c" : "#eff6ff") : (isDarkMode ? "#1e293b" : "#fff"), color: isDarkMode ? "#f1f5f9" : "#000" }}>All</Button>
                      {technicians.map((t) => (
                        <Button key={t} onClick={() => setTechFilter(t)} sx={{ textTransform: "none", borderRadius: 1, border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb", backgroundColor: techFilter === t ? (isDarkMode ? "#0f4a8c" : "#eff6ff") : (isDarkMode ? "#1e293b" : "#fff"), color: isDarkMode ? "#f1f5f9" : "#000" }}>{t}</Button>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>

            {isTechnician ? (
              <Box sx={{ mb: 2, mt: 3 }}>
                {!pendingOnly ? (
                  <Box>
                    <Button onClick={() => { setPendingOnly(true); setCategoryFilter('Pending'); }} sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: '#2563eb', color: '#fff', textTransform: 'none', fontWeight: 500, borderRadius: '8px', px: 2, '&:hover': { backgroundColor: '#1d4ed8' } }}>Pending Jobs (Pick up)</Button>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#0f172a' }}>Pending Jobs (Pick up)</Typography>
                      <Button onClick={() => { setPendingOnly(false); setCategoryFilter('all'); }} sx={{ textTransform: 'none', color: '#2563eb', fontWeight: 500 }}>Show All</Button>
                    </Box>
                    <Grid container spacing={1}>
                      {pendingJobs.slice(0, 6).map((pj) => (
                        <Grid item xs={12} sm={6} md={4} key={pj.id}>
                          <Card onClick={() => navigate(`/jobDetails/${encodeURIComponent(pj.id)}`)} sx={{ p: 2, borderRadius: 2, border: isDarkMode ? '1px solid #334155' : '1px solid #e5e7eb', cursor: 'pointer', backgroundColor: isDarkMode ? '#1e293b' : '#fff' }}>
                            <Typography sx={{ fontWeight: 600, color: isDarkMode ? '#f1f5f9' : '#0f172a' }}>{pj.displayRef || pj.ref || pj.id}</Typography>
                            <Typography sx={{ fontSize: 13, color: isDarkMode ? '#cbd5e1' : '#64748b' }}>{pj.customer}</Typography>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            ) : null}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", backgroundColor: isDarkMode ? '#1e293b' : '#fff' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography fontWeight={600} sx={{ color: isDarkMode ? '#f1f5f9' : '#0f172a' }}>Recent Jobs</Typography>
                    <Button sx={{ textTransform: "none", color: isDarkMode ? "#fff" : "#2563eb", fontSize: 13 }} onClick={handleViewAll}>View all ↗</Button>
                  </Box>

                  {loading ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <CircularProgress />
                    </Box>
                  ) : filteredJobs.length === 0 ? (
                    <Paper elevation={0} sx={{ py: 6, textAlign: "center", borderRadius: "12px", border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb", background: isDarkMode ? "#0f172a" : "#f9fafb" }}>
                      <Typography sx={{ color: isDarkMode ? '#cbd5e1' : '#374151' }}>No jobs found</Typography>
                    </Paper>
                  ) : (
                    filteredJobs.map((j) => (
                      <Card
                        key={j.id}
                        onClick={() => navigate(`/jobDetails/${encodeURIComponent(j.id)}`)}
                        sx={{ mb: 2, p: 3, borderRadius: "10px", border: isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb", cursor: 'pointer', backgroundColor: isDarkMode ? '#0f172a' : '#fff', '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.06)' } }}
                      >
                        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                          <Box flex={1}>
                            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                              <Box>
                                <Typography fontWeight={600} sx={{ color: isDarkMode ? '#f1f5f9' : '#0f172a' }}>{j.displayRef || j.jobNumber || j.ref || j.id}</Typography>
                                <Box mt={0.5}>
                                  {(() => {
                                    const s = (j.status || '').toString().toLowerCase();
                                    // stronger, high-contrast status colors
                                    const styles = {
                                      ready: { backgroundColor: isDarkMode ? '#166534' : '#bbf7d0', color: isDarkMode ? '#bbf7d0' : '#065f46' },
                                      completed: { backgroundColor: isDarkMode ? '#166534' : '#bbf7d0', color: isDarkMode ? '#bbf7d0' : '#065f46' },
                                      'in progress': { backgroundColor: isDarkMode ? '#134e4a' : '#99f6e4', color: isDarkMode ? '#99f6e4' : '#0f766e' },
                                      inprogress: { backgroundColor: isDarkMode ? '#134e4a' : '#99f6e4', color: isDarkMode ? '#99f6e4' : '#0f766e' },
                                      pending: { backgroundColor: isDarkMode ? '#92400e' : '#ffedd5', color: isDarkMode ? '#fcd34d' : '#92400e' },
                                      'on hold': { backgroundColor: isDarkMode ? '#7f1d1d' : '#fecaca', color: isDarkMode ? '#fca5a5' : '#7f1d1d' },
                                      hold: { backgroundColor: isDarkMode ? '#7f1d1d' : '#fecaca', color: isDarkMode ? '#fca5a5' : '#7f1d1d' },
                                      default: { backgroundColor: isDarkMode ? '#5b21b6' : '#e9d5ff', color: isDarkMode ? '#d8b4fe' : '#5b21b6' }
                                    };
                                    const key = Object.keys(styles).find(k => s.includes(k)) || 'default';
                                    return <Chip label={j.status || ''} size="small" sx={{ ...styles[key], fontSize: 12, fontWeight: 600, px: 1.2, py: 0.4, boxShadow: 'none' }} />
                                  })()}
                                  {j.createdByName ? (
                                    <Typography fontSize={12} color={isDarkMode ? "#94a3b8" : "#6b7280"} sx={{ mt: 0.5 }}><span style={{ fontWeight: 600, marginRight: 6 }}>Created by:</span>{j.createdByName}</Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            </Box>

                            <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1} mt={2}>
                              <Box display="flex" alignItems="flex-start" gap={1}>
                                <PersonIcon sx={{ fontSize: 18, color: isDarkMode ? '#64748b' : '#94a3b8', mt: 0.3 }} />
                                <Box>
                                  <Typography fontSize={13} color={isDarkMode ? "#f1f5f9" : "#0f172a"}>{j.customerName || j.customer || '-'}</Typography>
                                  {j.customerAddress && <Typography fontSize={12} color={isDarkMode ? "#94a3b8" : "#94a3b8"}>{j.customerAddress}</Typography>}
                                </Box>
                              </Box>

                              <Box display="flex" alignItems="flex-start" gap={1}>
                                <PhoneIcon sx={{ fontSize: 18, color: isDarkMode ? '#64748b' : '#94a3b8', mt: 0.3 }} />
                                <Box>
                                  <Typography fontSize={13} color={isDarkMode ? "#f1f5f9" : "#0f172a"}>{j.customerPhone ? formatPhone(j.customerPhone) : '-'}</Typography>
                                  {j.codNumber && <Typography fontSize={12} color={isDarkMode ? "#94a3b8" : "#94a3b8"}>COD: {j.codNumber}</Typography>}
                                </Box>
                              </Box>

                              <Box display="flex" alignItems="flex-start" gap={1}>
                                <Inventory2Icon sx={{ fontSize: 18, color: isDarkMode ? '#64748b' : '#94a3b8', mt: 0.3 }} />
                                <Box>
                                  {j.deviceName && <Typography fontSize={13} color={isDarkMode ? "#f1f5f9" : "#0f172a"}>{j.deviceName}</Typography>}
                                  <Typography fontSize={13} color={isDarkMode ? "#f1f5f9" : "#0f172a"}>{j.deviceModel || ''}</Typography>
                                  {j.serialNumber && <Typography fontSize={12} color={isDarkMode ? "#94a3b8" : "#94a3b8"}>SN: {j.serialNumber}</Typography>}
                                </Box>
                              </Box>

                              {j.technician && (
                                <Box display="flex" alignItems="flex-start" gap={1}>
                                  <PersonIcon sx={{ fontSize: 18, color: isDarkMode ? '#64748b' : '#94a3b8', mt: 0.3 }} />
                                  <Box>
                                    <Typography fontSize={12} color={isDarkMode ? "#94a3b8" : "#94a3b8"}>Assigned to</Typography>
                                          <Typography fontSize={13} color={isDarkMode ? "#f1f5f9" : "#0f172a"}>{(() => {
                                            if (!j.technician) return j.assignedToName || '-';
                                            // if it's already a friendly name, show it
                                            if (typeof j.technician === 'string' && j.technician.match(/^[A-Za-z\s@.]+$/)) return j.technician;
                                            const found = techUsers.find(t => String(t._id) === String(j.technician));
                                            if (found) return `${found.firstName || ''}${found.lastName ? ` ${found.lastName}` : ''}`.trim();
                                            return j.technician;
                                          })()}</Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>

                            { (j.faultDescription || j.desc) && (
                              <Box mt={2} pt={1} borderTop={isDarkMode ? "1px solid #334155" : "1px solid #eef2f7"}>
                                <Typography fontSize={12} color={isDarkMode ? "#94a3b8" : "#64748b"}>Fault Description</Typography>
                                <Typography fontSize={13} color={isDarkMode ? "#f1f5f9" : "#0f172a"}>{j.faultDescription || j.desc}</Typography>
                              </Box>
                            )}
                          </Box>

                          <Box sx={{ textAlign: { xs: 'left', md: 'right' }, minWidth: 140 }}>
                            <Box display="flex" alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} color={isDarkMode ? "#94a3b8" : "#64748b"}>
                              <EventIcon sx={{ fontSize: 14, mr: 1 }} />
                              <Typography fontSize={12}>{formatDate(j.createdAt || j.date)}</Typography>
                            </Box>
                            {(() => {
                              const costOfParts = Array.isArray(j.jobParts) ? j.jobParts.reduce((sum, p) => sum + (Number(p.cost || 0) * Number(p.quantity || 1)), 0) : 0;
                              const serviceCharge = Number(j.serviceCharge || 0);
                              const shouldHide = j.hideServiceCharge && isTechnician;
                              const total = costOfParts + (shouldHide ? 0 : serviceCharge);
                              return (serviceCharge > 0 && total > 0) ? (
                                <Typography sx={{ fontWeight: 700, fontSize: 16, color: isDarkMode ? '#f1f5f9' : '#111827', mt: 1 }}>Rs. {formatAmount(total)}</Typography>
                              ) : null;
                            })()}
                            {/* Print buttons removed from Recent Jobs — shown in Job Details only */}
                            {Array.isArray(j.usedParts) && j.usedParts.length > 0 && (
                              <Typography fontSize={12} color={isDarkMode ? "#94a3b8" : "#6b7280"} mt={1}>{j.usedParts.length} part{j.usedParts.length !== 1 ? 's' : ''} used</Typography>
                            )}
                            {(j.referJobNumber || j.referJobId) ? (
                              <Box sx={{ mt: 2, display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={(e) => { e.stopPropagation(); handleReferNavigate(j.referJobNumber || (j.referJobId && (j.referJobId.jobNumber || j.referJobId._id))); }}
                                  sx={{ textTransform: 'none', borderRadius: '8px', px: 1.5, backgroundColor: '#2563eb', color: '#fff', fontWeight: 500, '&:hover': { backgroundColor: '#1d4ed8' } }}
                                >
                                  Referred from {j.referJobNumber || (j.referJobId && (j.referJobId.jobNumber || j.referJobId._id))}
                                </Button>
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
        <CreateJobModal open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreateJob} initialRef={nextJobRef} />
      </Box>
    </>
  );
};

export default Jobs;
